import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DonutLargeOutlined } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { Card, Divider, Box, CircularProgress } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useTheme } from '@mui/material/styles';
import './WinRatePieChart.css';
import { usePeriod } from './PeriodContext';
import Tooltip from '@mui/material/Tooltip';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const WinRatePieChart = ({ userID, trades }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const calculateWinRate = () => {
      setLoading(true);
      const filteredTrades = trades.filter(entry => entry.status === (showMissedTrades ? 'Missed' : 'Closed'));

      if (filteredTrades.length === 0) {
        setHasData(false);
        setData([]);
        setLoading(false);
        return;
      }

      let wins = 0;
      let losses = 0;
      let breakevens = 0;
      let hasPeriodData = false;

      filteredTrades.forEach((entry) => {
        const entryDate = new Date(entry.date);
        const matchesPeriod = () => {
          if (selectedPeriod === 'today') {
            return entryDate.toDateString() === new Date().toDateString();
          } else if (selectedPeriod === 'this-week') {
            return entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'this-month') {
            return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'this-year') {
            return entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'pick-month') {
            return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
          } else if (selectedPeriod === 'pick-year') {
            return entryDate.getFullYear() === selectedYear;
          } else if (selectedPeriod === 'pick-month-year') {
            return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
          } else {
            return true; // all-time
          }
        };

        const matchesTag = selectedTag === 'all' || 
          (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));

        if (matchesPeriod() && matchesTag) {
          hasPeriodData = true;
          if (!isNaN(entry.returns)) {
            if (entry.returns > 0) {
              wins++;
            } else if (entry.returns < 0) {
              losses++;
            } else {
              breakevens++;
            }
          }
        }
      });

      if (!hasPeriodData) {
        setHasData(false);
        setData([]);
      } else {
        setHasData(true);
        const totalTrades = wins + losses + breakevens;
        const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
        const lossRate = totalTrades > 0 ? (losses / totalTrades) * 100 : 0;
        const breakevenRate = totalTrades > 0 ? (breakevens / totalTrades) * 100 : 0;

        setData([
          { id: 'Wins', value: winRate, color: '#5FB663' },
          { id: 'Losses', value: lossRate, color: '#EA4643' },
          { id: 'Breakeven', value: breakevenRate, color: '#FFA726' },
        ]);
      }
      setLoading(false);
    };

    if (userID) {
      calculateWinRate();
    }
  }, [userID, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag, trades]);

  const renderHighlightedLabel = (params) => {
    const { id, value } = params;
    return `${id}: ${value.toFixed(0)}%`;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }

    if (!hasData) {
      return (
        <MDBox 
          display="flex" 
          flexDirection="column" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available yet
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your performance charts
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox pb={2} px={2} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center">
        <MDBox 
          mr={{ xs: 0, sm: 2 }} 
          mb={{ xs: 2, sm: 0 }}
          width={{ xs: '100%', sm: 'auto' }}
        >
          {data.map(item => (
            <Box key={item.id} display="flex" alignItems="center" mb={1}>
              <Box
                width={16}
                height={16}
                sx={{ backgroundColor: item.color, borderRadius: '2px'}}
                mr={1}
              />
              <MDTypography variant="body2" color="text" className="label-text-pie" sx={{ fontSize: '0.95rem' }}>
                {item.id}: {item.value.toFixed(0)}%
              </MDTypography>
            </Box>
          ))}
        </MDBox>
        <MDBox
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            aspectRatio: '16/9',
            maxWidth: { xs: '200px', sm: '280px' },
            margin: '0 auto'
          }}
        >
          <PieChart
            width={280}
            height={190}
            series={[
              {
                innerRadius: 50,
                outerRadius: 70,
                data: data,
                dataKey: 'value',
                cornerRadius: 5,
                paddingAngle: 5,
                startAngle: 90,
                endAngle: 450,
                colors: data.map(item => item.color),
              }
            ]}
            highlightOptions={{
              highlightEnabled: true,
              highlightBy: 'value',
              highlightColors: data.map(item => item.color),
              highlightLabelFormatter: renderHighlightedLabel,
            }}
          />
        </MDBox>
      </MDBox>
    );
  };

  const tooltipContent = `The Win Rate Breakdown visually displays the percentage of profitable (green), unprofitable (red), and breakeven (orange) ${showMissedTrades ? 'missed' : 'closed'} trades, providing a clear overview of your trade outcomes. Overall win rate excludes breakeven trades.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" height="100%">
        <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <DonutLargeOutlined fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text">
              Win Rate {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
            <MDTypography variant="h5" fontFamily="inherit">
              {(() => {
                const wins = data.find(item => item.id === 'Wins')?.value || 0;
                const losses = data.find(item => item.id === 'Losses')?.value || 0;
                const actualWinRate = (wins / (wins + losses)) * 100 || 0;
                return `${actualWinRate.toFixed(1)}%`;
              })()}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

WinRatePieChart.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
};

export default WinRatePieChart;